import { Component } from '@theme/component';

/**
 * <seo-text-toggle-component> : bloc de texte SEO repliable ("Voir plus / Voir moins").
 *
 * Anime la hauteur du bloc de texte entre une hauteur repliée (variable CSS
 * --seo-text-collapsed-height, définie depuis les réglages de la section) et sa hauteur complète,
 * via la Web Animations API - performant, natif, sans jQuery. Suit le même schéma que
 * assets/show-more.js déjà utilisé par les filtres du thème, simplifié pour un bloc de texte
 * unique (pas de liste d'éléments à masquer individuellement).
 *
 * Respecte prefers-reduced-motion : le changement de hauteur est instantané (sans animation) si
 * l'utilisateur préfère moins d'animations.
 */
class SeoTextToggleComponent extends Component {
  requiredRefs = ['content', 'toggleButton'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {Animation | undefined} */
  #animation;

  /** @constant {number} */
  #animationSpeed = 350;

  /**
   * Bascule entre l'état replié et déplié du bloc de texte.
   *
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();

    const { content, toggleButton } = this.refs;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const startHeight = content.offsetHeight;
    this.#expanded = !this.#expanded;

    this.dataset.expanded = this.#expanded ? 'true' : 'false';
    toggleButton.setAttribute('aria-expanded', String(this.#expanded));

    const endHeight = this.#expanded ? content.scrollHeight : this.#collapsedHeight();

    if (prefersReducedMotion) {
      content.style.maxHeight = this.#expanded ? 'none' : '';
      return;
    }

    content.style.overflow = 'hidden';
    this.#animation?.cancel();
    this.#animation = content.animate(
      { maxHeight: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => {
      content.style.maxHeight = this.#expanded ? 'none' : '';
      content.style.overflow = this.#expanded ? '' : 'hidden';
    };
  };

  /**
   * Lit la hauteur repliée définie par la variable CSS --seo-text-collapsed-height (réglée en
   * ligne par la section depuis le paramètre "Hauteur visible avant troncature").
   *
   * @returns {number}
   */
  #collapsedHeight() {
    const value = getComputedStyle(this).getPropertyValue('--seo-text-collapsed-height');
    return parseFloat(value) || 120;
  }
}

if (!customElements.get('seo-text-toggle-component')) {
  customElements.define('seo-text-toggle-component', SeoTextToggleComponent);
}
